import React, { useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { ComoButton } from "../Common/ComoButton";
import color from "../../Constants/colors";
import {
  useUserMemory,
  useUpdateUserMemory,
  useDeleteUserMemory,
  useClearUserMemory,
} from "../../Query/useUsers";

interface UserMemoryProps {
  onClose?: () => void;
}

const iconBtnSx = {
  color: color.gray500,
  "&:hover": {
    bgcolor: color.gray100,
    color: color.accent,
  },
};

const UserMemory: React.FC<UserMemoryProps> = ({ onClose }) => {
  const { t } = useTranslation("profile");
  const { data, isLoading, refetch } = useUserMemory();

  const updateMutation = useUpdateUserMemory();
  const deleteMutation = useDeleteUserMemory();
  const clearMutation = useClearUserMemory();

  const [editingLine, setEditingLine] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [expectedText, setExpectedText] = useState("");

  const [error, setError] = useState<string | null>(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const memory = data?.memory ?? [];

  const handleStartEdit = (line: number, text: string) => {
    setError(null);
    setEditingLine(line);
    setEditText(text);
    setExpectedText(text);
  };

  const handleCancelEdit = () => {
    setEditingLine(null);
    setEditText("");
    setExpectedText("");
  };

  const handleSave = async () => {
    if (editingLine === null) return;

    setError(null);

    try {
      await updateMutation.mutateAsync({
        line: editingLine,
        text: editText,
        expected: expectedText,
      });

      handleCancelEdit();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        await refetch();
        handleCancelEdit();
        setError(t("profile.memoryChanged"));
        return;
      }

      setError(t("profile.memorySaveError"));
    }
  };

  const handleDelete = async (line: number, expected: string) => {
    setError(null);

    try {
      await deleteMutation.mutateAsync({
        line,
        expected,
      });
    } catch (error: any) {
      if (error?.response?.status === 409) {
        await refetch();
        handleCancelEdit();
        setError(t("profile.memoryChanged"));
        return;
      }

      setError(t("profile.memoryDeleteError"));
    }
  };

  const handleClearAll = async () => {
    setError(null);

    try {
      await clearMutation.mutateAsync();
      setClearDialogOpen(false);
      handleCancelEdit();
    } catch {
      setError(t("profile.memoryClearError"));
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 8,
        }}
      >
        <CircularProgress sx={{ color: color.accent }} />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          maxWidth: 960,
          mx: "auto",
          px: { xs: 2, md: 4 },
          py: 4,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <PsychologyIcon
              sx={{
                color: color.accent,
                fontSize: 28,
                flexShrink: 0,
                mt: 0.25,
              }}
            />

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: color.primary_dark,
                }}
              >
                {t("profile.agentMemoryTitle")}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: color.gray500,
                  mt: 0.25,
                }}
              >
                {t("profile.agentMemoryDescription")}
              </Typography>
            </Box>
          </Box>
          {onClose && (
            <IconButton
              onClick={onClose}
              sx={iconBtnSx}
              aria-label={t("profile.agentMemoryClose")}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        {error && (
          <Alert
            severity="warning"
            onClose={() => setError(null)}
            sx={{
              borderRadius: 2,
              mb: 2,
            }}
          >
            {error}
          </Alert>
        )}
        {memory.length === 0 ? (
          <Typography
            sx={{
              color: color.gray500,
              textAlign: "center",
              py: 8,
            }}
          >
            {t("profile.agentMemoryEmpty")}
          </Typography>
        ) : (
          <Box>
            {memory.map((item) => {
              const isEditing = editingLine === item.line;

              return (
                <Box
                  key={item.line}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    px: 2,
                    py: 1.5,
                    gap: 2,
                    borderBottom: `1px solid ${color.gray100}`,
                    "&:hover": {
                      backgroundColor: color.gray50,
                    },
                  }}
                >
                  {isEditing ? (
                    <TextField
                      value={editText}
                      onChange={(event) => setEditText(event.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={5}
                      size="small"
                      autoFocus
                      disabled={updateMutation.isPending}
                      sx={{
                        "& .MuiInputBase-root": {
                          backgroundColor: color.white,
                          fontSize: "0.875rem",
                          lineHeight: 1.6,
                          borderRadius: "8px",
                        },
                        "& .MuiInputBase-input": {
                          color: color.primary_dark,
                          WebkitTextFillColor: "currentColor",
                          fontSize: "0.875rem",
                          lineHeight: 1.6,
                          fontFamily: "inherit",
                          fontWeight: 400,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: color.gray200,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: color.gray300,
                        },
                        "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: color.accent,
                        },
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: color.primary_dark,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        lineHeight: 1.6,
                        minWidth: 0,
                      }}
                    >
                      {item.text}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      width: 76,
                      justifyContent: "flex-end",
                    }}
                  >
                    {isEditing ? (
                      <>
                        <IconButton
                          size="small"
                          onClick={handleSave}
                          disabled={updateMutation.isPending}
                          sx={iconBtnSx}
                        >
                          {updateMutation.isPending ? (
                            <CircularProgress size={18} />
                          ) : (
                            <CheckIcon sx={{ fontSize: 19 }} />
                          )}
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          disabled={updateMutation.isPending}
                          sx={iconBtnSx}
                        >
                          <CloseIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleStartEdit(item.line, item.text)}
                          disabled={
                            updateMutation.isPending || deleteMutation.isPending
                          }
                          sx={iconBtnSx}
                        >
                          <EditIcon sx={{ fontSize: 19 }} />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={() => handleDelete(item.line, item.text)}
                          disabled={
                            updateMutation.isPending || deleteMutation.isPending
                          }
                          sx={{
                            ...iconBtnSx,
                            "&:hover": {
                              bgcolor: color.gray100,
                              color: color.red700,
                            },
                          }}
                        >
                          {deleteMutation.isPending ? (
                            <CircularProgress size={18} />
                          ) : (
                            <DeleteOutlineIcon sx={{ fontSize: 19 }} />
                          )}
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {memory.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: "auto",
              pt: 3,
              width: "100%",
            }}
          >
            <ComoButton
              secondary
              startIcon={
                clearMutation.isPending ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <DeleteSweepOutlinedIcon sx={{ fontSize: 19 }} />
                )
              }
              onClick={() => setClearDialogOpen(true)}
              disabled={
                clearMutation.isPending ||
                updateMutation.isPending ||
                deleteMutation.isPending
              }
              sx={{
                px: 1.5,
                py: 0.7,
                fontSize: "0.8rem",
                minWidth: "auto",
              }}
            >
              {t("profile.agentMemoryClearAll")}
            </ComoButton>
          </Box>
        )}
      </Box>

      {/* Clear All Confirmation Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={() => {
          if (!clearMutation.isPending) {
            setClearDialogOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: color.primary_dark,
          }}
        >
          {t("profile.agentMemoryClearConfirmTitle")}
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            {t("profile.agentMemoryClearConfirmMessage")}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <ComoButton
            secondary
            onClick={() => setClearDialogOpen(false)}
            disabled={clearMutation.isPending}
          >
            {t("profile.agentMemoryCancel")}
          </ComoButton>

          <ComoButton
            onClick={handleClearAll}
            disabled={clearMutation.isPending}
            startIcon={
              clearMutation.isPending ? (
                <CircularProgress size={14} sx={{ color: "inherit" }} />
              ) : (
                <DeleteSweepOutlinedIcon />
              )
            }
          >
            {t("profile.agentMemoryClearAll")}
          </ComoButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserMemory;
